export type StartPath = 'sample' | 'real';
export type WizardStep = 'start' | 'project' | 'library' | 'signals' | 'selection' | 'done';

export type SampleLibraryCandidate = {
  is_sample_library?: boolean;
  library_source?: string;
  constraints?: Record<string, unknown> | null;
};

export const isSampleLibraryItem = (item: SampleLibraryCandidate) =>
  item.is_sample_library === true ||
  item.library_source === 'sample_library' ||
  (item.constraints &&
    (item.constraints as Record<string, unknown>).library_source ===
      'sample_library');

export const isSampleOnlyLibrary = (items: SampleLibraryCandidate[]) =>
  items.length > 0 && items.every(isSampleLibraryItem);

export const deriveProjectMode = ({
  hasTestLibrary,
  sampleLibraryLoaded,
  hasProject,
  startPath,
}: {
  hasTestLibrary: boolean;
  sampleLibraryLoaded: boolean;
  hasProject: boolean;
  startPath: StartPath;
}): StartPath | null =>
  hasTestLibrary
    ? sampleLibraryLoaded
      ? 'sample'
      : 'real'
    : hasProject
      ? startPath
      : null;

export const deriveSignalsStepComplete = ({
  projectMode,
  functionalHistoryCount,
  ownDataModeActive,
  hasHistory,
  hasObservability,
  observabilitySkipped,
}: {
  projectMode: StartPath | null;
  functionalHistoryCount: number;
  ownDataModeActive: boolean;
  hasHistory: boolean;
  hasObservability: boolean;
  observabilitySkipped: boolean;
}) => {
  const sampleHistoryReady = deriveSampleHistoryReady(
    projectMode,
    functionalHistoryCount
  );

  return projectMode === 'sample'
    ? sampleHistoryReady
    : ownDataModeActive
      ? hasHistory || hasObservability || observabilitySkipped
      : false;
};

export const deriveSampleHistoryReady = (
  projectMode: StartPath | null,
  functionalHistoryCount: number
) => projectMode === 'sample' && functionalHistoryCount > 0;

export const deriveWizardStep = ({
  canShowStartOptions,
  hasProject,
  hasTestLibrary,
  signalsStepComplete,
  hasRunSelection,
}: {
  canShowStartOptions: boolean;
  hasProject: boolean;
  hasTestLibrary: boolean;
  signalsStepComplete: boolean;
  hasRunSelection: boolean;
}): WizardStep =>
  canShowStartOptions
    ? 'start'
    : !hasProject
      ? 'project'
      : !hasTestLibrary
        ? 'library'
        : !signalsStepComplete
          ? 'signals'
          : !hasRunSelection
            ? 'selection'
            : 'done';

export const getNextRecommendedStep = <
  T extends { key: string; complete: boolean; optional: boolean }
>(
  checklist: T[]
) =>
  checklist.find((step) => !step.complete && !step.optional)?.key ||
  checklist.find((step) => !step.complete)?.key ||
  null;
